import React, { useState } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';

import Button from '../../components/common/Button';
import AuthLayout from '../../components/layout/AuthLayout';
import { useCommunicationApi } from '../../hooks/useCommunicationApi';

const CancelApplication: React.FC = () => {
  const [isCancelled, setIsCancelled] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { cancelRegistration, isLoading } = useCommunicationApi();

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  if (!token) {
    return (
      <AuthLayout
        title="Cancel Application"
        description="We're sorry, but we couldn't process your request because the token is missing."
      >
        <div className="mt-8">
          <p className="text-sm text-gray-400 text-center">
            Please check the link you received or contact us at
            Accounts@natshoot.co.za for assistance.
          </p>
          <div className="mt-6">
            <Button onClick={() => navigate('/')} className="w-full">
              Go Back to Home
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  const handleCancel = async (): Promise<void> => {
    setErrorMessage(null);
    try {
      const response = await cancelRegistration({ token });

      if (response && response.success) {
        setIsCancelled(true);
      } else {
        setErrorMessage(
          response?.message ??
            'We encountered an issue processing your request. Please try again later.',
        );
      }
    } catch (error) {
      console.error('Error cancelling registration:', error);
      setErrorMessage('An unexpected error occurred. Please try again later.');
    }
  };

  if (isCancelled) {
    return (
      <AuthLayout
        title="Application Cancelled"
        description="Your membership application has been cancelled and your information will be removed from our records."
      >
        <div className="mt-8">
          <p className="text-sm text-gray-400 text-center">
            If you change your mind, you're welcome to register again at any
            time.
          </p>
          <div className="mt-6">
            <Button onClick={() => navigate('/')} className="w-full">
              Go Back to Home
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Cancel Your Application"
      description="We're sorry to see you go. Cancelling will remove your membership application and personal information from our records."
    >
      <div className="mt-8">
        {errorMessage && (
          <div className="mb-4 text-sm text-red-600">{errorMessage}</div>
        )}
        <p className="text-sm text-gray-500 text-center">
          Are you sure you want to cancel your Natshoot membership
          application? This cannot be undone.
        </p>
        <div className="mt-6 space-y-3">
          <Button
            onClick={handleCancel}
            className="w-full"
            disabled={isLoading}
            isLoading={isLoading}
          >
            Yes, cancel my application
          </Button>
          <Button
            onClick={() => navigate('/')}
            variant="secondary"
            className="w-full"
            disabled={isLoading}
          >
            No, keep my application
          </Button>
        </div>
      </div>
    </AuthLayout>
  );
};

export default CancelApplication;
